import { usePermissions } from "@/Composables";
import { hasRouteValue } from "@/Composables/useRouteSupport";
import {
  BookOpenIcon,
  CalculatorIcon,
  CalendarIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  HomeIcon,
  MapPinIcon,
  MegaphoneIcon,
  RectangleStackIcon,
  SquaresPlusIcon,
  TruckIcon,
  UserGroupIcon,
} from "@heroicons/vue/24/outline";
import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";

export default function memberNavigation() {
  const page = usePage();
  const user = computed(() => page.props?.auth?.user);
  const orderBuilderTool = computed(() => page.props?.orderBuilderTool);
  const summitEventId = computed(() => page.props?.summit?.id ?? null);
  const { can } = usePermissions();

  const navigation = [
    { name: "Dashboard", route: "dashboard.index", icon: HomeIcon, component: "Member/Dashboard" },
    { name: '"Blue Book"', route: "blue-book-platform.index", icon: BookOpenIcon },
    {
      name: "Calendar of Events",
      route: "upcoming-events.index",
      component: "UpcomingEvents/Index",
      icon: CalendarIcon,
    },
    ...(can("list claims")
      ? [
        {
          name: "Accounting",
          icon: CalculatorIcon,
          children: [
            { name: "Invoices", route: "invoices.index", component: "Member/Invoices" },
            {
              name: "Invoice Research",
              route: "invoice-research.index",
              component: "Member/InvoiceResearches",
            },
            { name: "Claims", route: "claims.index", component: "Claims/Index" },
          ],
        },
      ]
      : []),
    {
      name: "Warehouse Programs Information",
      icon: TruckIcon,
      children: [
        { name: "Catalog", route: "warehouse.catalog.index", component: "Warehouse/Catalog/Index" },
        // { name: "Reports", route: "warehouse.reports.index", component: "Warehouse/Reports/Index" },
        {
          name: "Private Brands",
          route: "warehouse.private-brands.index",
          component: "Warehouse/PrivateBrands/Index",
        },
        {
          name: "Marketing Assets",
          route: "warehouse.marketing-assets.index",
          component: "Warehouse/MarketingAssets/Index",
        },
        { name: "Item Master", route: "warehouse.item-master.index", component: "Warehouse/ItemMaster/Index" },
        {
          name: "Forecast Database",
          route: "forecasting.index",
          component: "Forecasting/Index",
        },
        { name: "Defective Form", route: "warehouse.defective-form.index", download: true },
        { name: "Media Valet", route: "warehouse.media-valet.index", component: "Warehouse/MediaValet/Index" },
        {
          name: "Domo Item & Inv. Info",
          href: "https://msdist.domo.com/app-studio/19408487/pages/299929680",
          target: "_blank",
          external: true,
        },
        {
          name: "Member Sales Opportunity Report (GAP)",
          href: "https://msdist.domo.com/app-studio/1124781699/pages/1214492197",
          target: "_blank",
          external: true,
        },
        // {
        //   name: "Stage Gate Projects",
        //   route: "stage-gate-projects.index",
        //   component: "StageGateProjects",
        //   class: "nav-stage-gate-projects",
        // }

        ...(orderBuilderTool
          ? [
            {
              name: "Order Builder Tool",
              href: orderBuilderTool.value,
              target: "_blank",
              external: true,
            },
          ]
          : []),
      ],
    },
    {
      name: "Contacts",
      icon: UserGroupIcon,
      children: [
        {
          name: "MSD Board Members",
          route: "contacts.msd-board-members.index",
          component: "Contacts/MsdBoardMembers",
        },
        {
          name: "MSD Contacts",
          route: "contacts.msd-contacts.index",
          component: "Contacts/MsdContacts",
        },
        {
          name: "Member Companies",
          route: "contacts.member-companies.index",
          component: "Contacts/MemberCompanies",
        },
        {
          name: "Suppliers",
          route: "contacts.suppliers.index",
          component: "Contacts/Suppliers",
        },
        {
          name: "Rep Groups",
          route: "contacts.reps.index",
          component: "Contacts/Reps",
        },
        {
          name: "My Company Profile/Contacts",
          route: "company-profile.index",
          component: "CompanyProfile/Index",
        },
      ],
    },
    {
      name: "Reports",
      icon: ChartBarIcon,
      children: [
        {
          name: "Current Closeouts",
          route: "blue-book-platform.reports.current-closeouts.index",
          component: "BlueBookPlatform/Reports/CurrentCloseouts/Index",
        },
        {
          name: "Recent Blue Book/Price List Updates",
          href: route("blue-book-platform.recent-updates.index"),
          // component: "Member/Invoices",
          target: "_blank",
        },
        {
          name: "Blue Book Program List",
          route: "blue-book-platform.reports.programs.index",
          component: "BlueBookPlatform/Reports/Programs/Index",
        },
      ],
    },
    {
      name: "Shows",
      icon: CurrencyDollarIcon,
      children: [
        {
          name: "Upcoming/Next Shows",
          route: "events.next-shows.index",
          component: "Events/NextShows/Index",
        },
        {
          name: "Archived Shows",
          route: "events.archived-shows.index",
          component: "Events/ArchivedShows/Index",
        },
        ...(hasRouteValue(summitEventId.value)
          ? [
            {
              name: "Summit Registration",
              route: "events.event.summit.registration",
              params: { event: summitEventId.value, company_id: user.value?.company_id },
              component: "Events/Summit/Registration",
            },
          ]
          : []),
      ],
    },
    // { name: "Contacts", route: "contacts.index", icon: UserGroupIcon, component: "Contacts" },
    { name: "Committees", route: "committees.index", icon: RectangleStackIcon, component: "Committees/Index" },
    {
      name: "Sent Communications",
      route: "communications.sent.index",
      icon: MegaphoneIcon,
      component: "InLook",
    },
    // { name: "Messages", action: () => state.toggleMessages(true), icon: ChatBubbleBottomCenterTextIcon },
    // {
    //   name: "Events",
    //   icon: CalendarIcon,
    //   children: [
    //     { name: "Event Calendar", route: "upcoming-events.index", component: "UpcomingEvents/Index" },
    //     // { name: "Event Registration", route: "events.registration.index", component: "Events/Registration/Index" },
    //     // { name: "Virtual Shows - Active", route: "events.index", component: "Events/Index" },
    //     // {
    //     //   name: "Virtual Shows - Archived",
    //     //   route: "events.archived.index",
    //     //   component: "Events/Archived/Index",
    //     // },

    //     { name: "Virtual Shows", route: "events.index", component: "Events" },
    //   ],
    // },
    { name: "Member Map", route: "locations.index", icon: MapPinIcon, component: "Locations/Index" },
    // { name: "Reports", route: "reports.index", icon: ChartBarIcon, component: "Reports/Index" },
    {
      name: "Central Services",
      defaultOpen: true,
      icon: SquaresPlusIcon,
      children: [
        { name: "3E", href: "https://3eco.com", target: "_blank", external: true },
        { name: "Weather Trends", href: "https://weathertrends360.com", target: "_blank", external: true },
        { name: "National Shooting Sports", href: "https://www.nssf.org", target: "_blank", external: true },
      ],
    },
  ];

  return {
    navigation,
  };
}
